import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/solid";
import { useHistory, useParams } from "react-router-dom";
import _get from "lodash/get";
import { format } from "date-fns";
import { Formik, Field } from "formik";

import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import Tabs from "components/lib/Shared/Tabs";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import GeneralInformation from "./GeneralInformation";
import ResidentialDetails from "./ResidentialDetails";
import OfficialShareAllocation from "./OfficialShareAllocation";
import AccessRequestButton from "components/lib/Shared/CompanyDashboard/AccessRequestButton";
import RequestConfirmationModal from "components/lib/Shared/CompanyDashboard/RequestConfirmationModal";
import { getDocumentData } from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import { Constants } from "config/constants";
import { getOfficial, resetOfficial } from "state/slices/companyDashboard";
import { closeModal } from "state/slices/modals";
import { addAccessRequest } from "state/slices/accessRequest";
import { useToast } from "hooks/useToast";
import AssignPermissionsSelection from "components/lib/Shared/CompanyDashboard/AssignPermission/Selection";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import { getBeneficialOwnershipFormFields, getFullName } from "utils";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import TransactionEditContext from "context/TransactionEditContext";
import { is } from "date-fns/locale";

const MODAL_NAME = "initiate_request_confirmation_modal_company_dashboard";

function getChangeDocumentFiles(currentTransaction, reason, official) {
  if (!currentTransaction) return [];
  switch (reason) {
    case "resignation":
      return [
        ...(currentTransaction.resignationLetter || []),
        ...(currentTransaction.statutoryDeclaration || []),
      ];
    case "removal":
      return [
        ...(currentTransaction.noticeOfRemoval || []),
        ...(currentTransaction.shareholdersSpecialNotice || []),
      ];
    case "death":
      return currentTransaction.deathCertificate || [];
    case "bankruptcy":
      return currentTransaction.insolvencyOrder || [];
    case "insanity":
      return currentTransaction.medicalReport || [];
    case "courtOrder":
      return currentTransaction.courtOrder || [];
    case "retirement":
    case "rotation":
      return []; // No uploads required
    default:
      return (
        currentTransaction.changeDocuments ||
        official.changeDocuments ||
        official.fileUploads ||
        []
      );
  }
}

function CompanyOfficialSubRow({
  isInChangesModule,
  visibleColumns,
  isChangeList,
  isHistory,
  row,
  data,
  tableData,
  currentTab,
  initialRows,
  latestVersion,
  previousVersion,
  setChangeOfparticularsMarker,
}) {
  const history = useHistory();
  const params = useParams();
  const { toast } = useToast(5000);
  const [tabs, setTabs] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const dispatch = useDispatch();
  let {
    company,
    official,
    getOfficialStatus,
    dashboardPermissions,
    dashboardRoles,
    currentUserRow,
    isCurrentUserCorporateRow,
    designation,
  } = useSelector((state) => state.companyDashboardSlice);

  /** Get official from previous history */
  if (isHistory && previousVersion?.length > 0) {
    official = previousVersion?.find((off) => off?._id === row?.original?._id);
  }
  const { trxId } = useParams();
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  const currentTransaction = initiatedTransactions?.items?.find(
    (transaction) => transaction._id === trxId
  );

  const { DIRECTOR, AUTHORIZED_PERSON, COMPANY_SECRETARY } =
    Constants.APPLICANT_CAPACITIES;
  const permissionsMap = new Map();
  const createAndMergePermissions = (roleConstant) => {
    const permissionsAgainstRole =
      Roles_Permissions_Constants.createRequestPermissions(
        roleConstant,
        designation
      );
    permissionsAgainstRole.forEach((permission) => {
      const dashboardPermission = dashboardPermissions.find(
        (p) => p.name === permission.name
      );
      const isDefault = dashboardPermission
        ? dashboardPermission.isDefault
        : false;

      if (permissionsMap.has(permission.name)) {
        permissionsMap.set(permission.name, {
          ...permission,
          isDefault: permissionsMap.get(permission.name).isDefault || isDefault,
        });
      } else {
        permissionsMap.set(permission.name, {
          ...permission,
          isDefault,
        });
      }
    });
  };
  dashboardRoles?.forEach((role) => createAndMergePermissions(role.name));
  const permissionsToBeRequested = Array.from(permissionsMap.values());
  const isCompanyDashboard =
    history.location.pathname.split("/").pop() === "dashboard";
  const viewRestrictedData = dashboardPermissions.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.VIEW_RESTRICTED_DATA
  );
  const currentUser = useSelector((state) => state.userSlice);
  const { isInTransactionEditContext } = useContext(TransactionEditContext);
  const { items } = useSelector((state) => state.accessRequestsSlice);

  const isCurrentUserRow =
    official?.electronicAddress?.emailAddress === currentUser?.email;

  const showLimitedData = !isCurrentUserRow && !viewRestrictedData;

  const accessRequests = items.filter(
    (item) =>
      item.status === "Pending" &&
      item.page === "COMPANY_DASHBOARD" &&
      item.company?._id === company._id
  );

  const showRequestButton =
    accessRequests.length === 0 &&
    showLimitedData &&
    dashboardPermissions.some(
      (permission) =>
        permission.name ===
        Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.SHOW_REQUEST_BUTTON
    );

  useEffect(() => {
    if (row?.original?._id) {
      dispatch(
        getOfficial({
          companyId: params.companyId,
          officialId: row?.original?._id,
          queryParams: {
            idType: row?.original?.idType || row.original.official.idType,
            JSGroupID: row?.original?.JSGroupID,
            isHistoryReview: true,
          }

        })
      );
    }
    return () => {
      dispatch(resetOfficial());
    };
  }, [row?.original?._id, dispatch, params.companyId]);

  useEffect(() => {
    let tempTabs = [];
    if (isChangeList) {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General Information",
        },
        {
          id: "changeDocuments",
          label: "Change Documents",
        },
      ];
    } else if (isInChangesModule) {
      tempTabs = [
        {
          id: "transactionDetails",
          label: "Transaction Details",
        },
        {
          id: "officialShareAllocation",
          label: "Share Information",
        },
        {
          id: "officialBeneficialOwnership",
          label: "Beneficial Ownership",
        },
        {
          id: "changeDocuments",
          label: "Change Documents",
        },
      ];
    } else {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
        },
        {
          id: "residentialDetails",
          label: "Address details",
        },
      ];
      if (isCurrentUserRow) {
        tempTabs.push({
          id: "uploadDocuments",
          label: "File Uploads",
        });
        if (
          ![DIRECTOR, AUTHORIZED_PERSON, COMPANY_SECRETARY].includes(
            row.original.designation
          )
        ) {
          tempTabs.push(
            {
              id: "officialShareAllocation",
              label: "Share information",
            },
            {
              id: "officialBeneficialOwnership",
              label: "Beneficial ownership",
            }
          );
        }
      } else if (
        [DIRECTOR, AUTHORIZED_PERSON, COMPANY_SECRETARY].includes(
          row.original.designation
        ) &&
        viewRestrictedData
      ) {
        tempTabs.push({
          id: "uploadDocuments",
          label: "File Uploads",
        });
      } else if (
        ![DIRECTOR, AUTHORIZED_PERSON, COMPANY_SECRETARY].includes(
          row.original.designation
        )
      ) {
        if (viewRestrictedData) {
          tempTabs.push(
            {
              id: "officialShareAllocation",
              label: "Share information",
            },
            {
              id: "officialBeneficialOwnership",
              label: "Beneficial ownership",
            },
            {
              id: "uploadDocuments",
              label: "File Uploads",
            }
          );
        } else {
          tempTabs.push({
            id: "officialShareAllocation",
            label: "Share information",
          });
        }
      }
      if (currentTab === "beneficialOwnership") {
        tempTabs = [
          {
            id: "generalInformation",
            label: "General information",
          },
          {
            id: "residentialDetails",
            label: "Address details",
          },
          {
            id: "officialBeneficialOwnership",
            label: "Beneficial ownership",
          },
          {
            id: "uploadDocuments",
            label: "File Uploads",
          },
        ];
      }
    }
    setTabs(tempTabs);
  }, [
    row.original.designation,
    currentTab,
    isCurrentUserRow,
    row,
    official,
    viewRestrictedData,
    isInChangesModule,
    isChangeList,
    DIRECTOR,
    AUTHORIZED_PERSON,
    COMPANY_SECRETARY,
  ]);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const goToPrevStep = () => {
    if (selectedTabIndex > 0) {
      setSelectedTabIndex(selectedTabIndex - 1);
    }
  };

  const goToNextStep = () => {
    if (selectedTabIndex < tabs.length - 1) {
      setSelectedTabIndex(selectedTabIndex + 1);
    }
  };

  useEffect(() => {
    const tempSelectedTab = tabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [tabs, selectedTabIndex]);

  if (!official || getOfficialStatus.status === "loading") {
    return (
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className="px-9">
          <div className="flex justify-center mx-auto m-2">
            <SpinnerIcon className="text-gray-400" />
          </div>
        </td>
      </tr>
    );
  }

  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = tabs.length - 1 === selectedTabIndex;

  let dateOfBirth = _get(row.original.identificationDetails, "dateOfBirth");
  if (dateOfBirth) dateOfBirth = format(new Date(dateOfBirth), "yyyy/MM/dd");
  else dateOfBirth = "N/A";

  const handleConfirmDetails = () => {
    row.toggleRowExpanded();
    const nextRow = initialRows?.[row.index + 1] || null;
    if (nextRow) nextRow?.getToggleRowExpandedProps()?.onClick();
  };

  const handleInitiateAccess = async (values) => {
    try {
      await dispatch(
        addAccessRequest({
          data: {
            permissions: values.dashboardPermissions,
            permissionsAccessType: values.accessType,
            startDate: values.startDate,
            endDate: values.endDate,
            initiatedBy: currentUser._id,
            role:
              currentUserRow?.designation ??
              isCurrentUserCorporateRow.designation,
            individual: currentUserRow?._id,
            corporate: isCurrentUserCorporateRow?._id,
            company: params.companyId,
            page: "COMPANY_DASHBOARD",
          },
        })
      );
      toast(
        "success",
        "The request has been successfully submitted to the registrar."
      );
      row?.toggleRowExpanded();
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong please try again."
      );
    }
  };

  const handleShowAccessRequestTab = () => {
    setTabs((prevTabs) => [
      ...prevTabs,
      {
        id: "requestAccess",
        label: "Request Access",
      },
    ]);
    setSelectedTabIndex(tabs.length);
    dispatch(closeModal(MODAL_NAME));
  };

  const transactionDetails = isInChangesModule
    ? (() => {
      const shareholder = tableData.find(
        (item) => item._id === row.original._id
      );
      if (shareholder) {
        return {
          dateOfOffer: shareholder.dateOfOffer
            ? format(new Date(shareholder.dateOfOffer), "yyyy/MM/dd")
            : "N/A",
          classOfShares: shareholder.classOfShares || "N/A",
          offerPricePerShare: shareholder.offerPricePerShare || "N/A",
          premiumPerShare: shareholder.premiumPerShare || "N/A",
          allottedShares: shareholder.allottedShares || "0",
          additionalSharesAllotted:
            shareholder.additionalSharesAllotted || "0",
        };
      }
      return {
        dateOfOffer: "N/A",
        classOfShares: "N/A",
        offerPricePerShare: "N/A",
        premiumPerShare: "N/A",
        allottedShares: "0",
        additionalSharesAllotted: "0",
      };
    })()
    : {
      transactionDate: official?.transactionDate
        ? format(new Date(official.transactionDate), "yyyy/MM/dd")
        : "N/A",
      typeOfChange: official?.typeOfChange || "N/A",
      status: official?.status || "N/A",
    };

  return (
    <tr className="bg-[#EDF0F5]">
      <RequestConfirmationModal
        modalName={MODAL_NAME}
        handleShowAccessRequestTab={handleShowAccessRequestTab}
      />
      <td
        colSpan={visibleColumns.length}
        className={`px-9 ${isCompanyDashboard ? "pb-7" : ""}`}
      >
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          PostPendComponent={
            showLimitedData &&
              showRequestButton &&
              !tabs.find((tab) => tab.id === "requestAccess") ? (
              <AccessRequestButton modalName={MODAL_NAME} />
            ) : null
          }
        />
        {selectedTab?.id === "generalInformation" && (
          <div className="px-4 pt-5 pb-6 rounded">
            <GeneralInformation
              official={official}
              isHistory={isHistory}
              latestVersion={latestVersion}
              isInTransactionEditContext={isInTransactionEditContext}
              isCompanyDashboard={isCompanyDashboard}
              data={data}
              isChangeList={isChangeList}
              isCurrentUserRow={isCurrentUserRow}
              viewRestrictedData={viewRestrictedData}
              hideResolveAllCheckbox
              setChangeOfparticularsMarker={setChangeOfparticularsMarker}
            />
          </div>
        )}
        {selectedTab?.id === "residentialDetails" && (
          <ResidentialDetails
            official={official}
            isHistory={isHistory}
            latestVersion={latestVersion}
            isCurrentUserRow={isCurrentUserRow}
            viewRestrictedData={viewRestrictedData}
            hideResolveAllCheckbox
          />
        )}
        {selectedTab?.id === "transactionDetails" && (
          <div className="px-8 pt-5 pb-6 mt-6 bg-white">
            <h3 className="mt-2 text-md font-medium text-sflPrimary">
              Transaction Details
            </h3>
            <hr />
            <div className="grid grid-cols-1 mt-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-sflPrimary">
                  Share Class:
                </label>
                <p className="mt-1 text-sm text-primary">
                  {isInChangesModule
                    ? transactionDetails.classOfShares
                    : transactionDetails.typeOfChange}
                </p>
              </div>
              {isInChangesModule && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-sflPrimary">
                      Premium per Share:
                    </label>
                    <p className="mt-1 text-sm text-primary">
                      {transactionDetails.premiumPerShare}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-sflPrimary">
                      Shares on Offer
                    </label>
                    <p className="mt-1 text-sm text-primary">
                      {transactionDetails.additionalSharesAllotted}
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-sflPrimary">
                  Price per Share:
                </label>
                <p className="mt-1 text-sm text-primary">
                  {isInChangesModule
                    ? transactionDetails.offerPricePerShare
                    : transactionDetails.status}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-sflPrimary">
                  Date of Offer:
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {isInChangesModule
                    ? transactionDetails.dateOfOffer
                    : transactionDetails.transactionDate}
                </p>
              </div>
            </div>
          </div>
        )}
        {selectedTab?.id === "officialShareAllocation" && (
          <OfficialShareAllocation
            tableData={tableData}
            isInChangesModule={isInChangesModule}
            sharesAllotted={row.original.sharesAllotted}
          />
        )}
        {selectedTab?.id === "officialBeneficialOwnership" && (
          <div className="overflow-hidden">
            <FormFieldsCard
              fields={getBeneficialOwnershipFormFields({
                beneficialOwnershipForm: official.beneficialOwnershipForm,
                reviewTab: true,
                beneficialOwner: official,
                beneficialOwners: [],
              })}
              formName="beneficialOwnershipForm"
              formHelperKey="beneficialOwnershipForm"
              hideResolveAllCheckbox
            />
            <FormFieldsCard
              fields={getBeneficialOwnershipFormFields({
                beneficialOwnershipForm: official.beneficialOwnershipForm,
                reviewTab: true,
                renderShortTypeOfControl: true,
                onlyDates: true,
                beneficialOwner: official,
                beneficialOwners: [],
              })}
              formName="beneficialOwnershipForm"
              formHelperKey="beneficialOwnershipForm"
              hideResolveAllCheckbox
            />
          </div>
        )}
        {selectedTab?.id === "uploadDocuments" && (
          <div className="px-8 pt-5 pb-6 mt-6 bg-white">
            <h3 className="mb-2 text-lg">Uploaded incorporation forms</h3>
            {official?.jointShareholders ? (
              official?.jointShareholders.map((jointShareholder) => (
                <div key={jointShareholder._id}>
                  <h4 className="mb-2 font-medium">
                    {getFullName(jointShareholder.names)}
                  </h4>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      fileUploads: jointShareholder.fileUploads,
                    }}
                    onSubmit={() => null}
                  >
                    {() => (
                      <div className="pl-4">
                        <Field
                          name="fileUploads"
                          component={FileUploadForm}
                          documentData={getDocumentData(
                            jointShareholder.idType
                          )}
                          onlyPreview
                          showDocumentLabel
                          hideResolveAllCheckbox
                          resolveAllFieldsCheckbox
                        />
                      </div>
                    )}
                  </Formik>
                </div>
              ))
            ) : (
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: official.fileUploads,
                }}
                onSubmit={() => null}
              >
                {() => (
                  <div className="pl-4">
                    <Field
                      name="fileUploads"
                      component={FileUploadForm}
                      documentData={getDocumentData(official.idType)}
                      onlyPreview
                      showDocumentLabel
                      hideResolveAllCheckbox
                      resolveAllFieldsCheckbox
                    />
                  </div>
                )}
              </Formik>
            )}
          </div>
        )}
        {selectedTab?.id === "changeDocuments" && (
          <div className="px-8 pt-5 pb-6 mt-6 mb-6 rounded bg-white">
            {isChangeList ? (
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: getChangeDocumentFiles(
                    currentTransaction,
                    currentTransaction?.processType ||
                    currentTransaction?.reason,
                    official
                  ),
                }}
                onSubmit={() => null}
              >
                {() => (
                  <div className="pl-4">
                    <p> the future of us</p>
                    <Field
                      name="fileUploads"
                      component={FileUploadForm}
                      documentData={getDocumentData(official.idType)}
                      onlyPreview
                      showDocumentLabel
                      hideResolveAllCheckbox
                      resolveAllFieldsCheckbox
                    />
                  </div>
                )}
              </Formik>
            ) : official?.jointShareholders ? (
              official?.jointShareholders.map((jointShareholder) => (
                <div key={jointShareholder._id}>
                  <h4 className="mb-2 font-medium">
                    {getFullName(jointShareholder.names)}
                  </h4>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      fileUploads:
                        jointShareholder.changeDocuments ||
                        jointShareholder.fileUploads,
                    }}
                    onSubmit={() => null}
                  >
                    {() => (
                      <div className="pl-4">
                        <Field
                          name="fileUploads"
                          component={FileUploadForm}
                          documentData={getDocumentData(
                            jointShareholder.idType
                          )}
                          onlyPreview
                          showDocumentLabel
                          hideResolveAllCheckbox
                          resolveAllFieldsCheckbox
                        />
                      </div>
                    )}
                  </Formik>
                </div>
              ))
            ) : (
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: official.changeDocuments || official.fileUploads,
                }}
                onSubmit={() => null}
              >
                {() => (
                  <div className="pl-4">
                    <Field
                      name="fileUploads"
                      component={FileUploadForm}
                      documentData={getDocumentData(official.idType)}
                      onlyPreview
                      showDocumentLabel
                      hideResolveAllCheckbox
                      resolveAllFieldsCheckbox
                    />
                  </div>
                )}
              </Formik>
            )}
          </div>
        )}
        {selectedTab?.id === "requestAccess" && (
          <div className="mt-4">
            <AssignPermissionsSelection
              initialValues={{
                accessType: "indefinite",
                dashboardPermissions: [],
                dashboardRoles: [],
              }}
              hasRoles={false}
              permissionsToBeRequested={permissionsToBeRequested}
              handleSubmit={handleInitiateAccess}
              submitBtnLabel={"Request Access"}
              loadingText={"Requesting..."}
            />
          </div>
        )}
        {!isCompanyDashboard && !isInTransactionEditContext && (
          <div className="flex justify-between">
            {!isFirstStep && (
              <button
                onClick={goToPrevStep}
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <span className="flex items-center mr-4 h-7">
                  <ChevronLeftIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
                <p className="font-semibold">Go back</p>
              </button>
            )}
            {isLastStep ? (
              <button
                onClick={() => handleConfirmDetails()}
                className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7"
              >
                <p>Close</p>
              </button>
            ) : (
              <button
                onClick={goToNextStep}
                className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
              >
                <p className="font-semibold">Review next step </p>
                <span className="flex items-center ml-4 h-7">
                  <ChevronRightIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default CompanyOfficialSubRow;
